import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  DirectionsBus,
  Visibility,
  ArrowBack,
  ContentCopy,
  NavigateNext,
  People,
  Settings,
  CalendarMonth,
  BookOnline,
  Lock,
  Refresh,
  VpnKey,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import api from '../../services/api';
import ShuttleScheduler from '../../components/shuttle/ShuttleScheduler';
import ShuttleBookingsList from '../../components/shuttle/ShuttleBookingsList';

interface Club {
  record_id: string;
  club_name: string;
}

interface ShuttleBus {
  id: string;
  club_id: string;
  name: string;
  capacity: number;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ShuttleStats {
  totalSchedules: number;
  activeSchedules: number;
  totalBookings: number;
  todayBookings: number;
}

type ViewMode = 'list' | 'detail';
type DetailTab = 'schedules' | 'bookings';

const ShuttleManagementPageV2: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  
  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [detailTab, setDetailTab] = useState<DetailTab>('schedules');
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [selectedClub, setSelectedClub] = useState<string>('');
  const [shuttles, setShuttles] = useState<ShuttleBus[]>([]);
  const [selectedShuttle, setSelectedShuttle] = useState<ShuttleBus | null>(null);
  const [shuttleStats, setShuttleStats] = useState<Record<string, ShuttleStats>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [driverPin, setDriverPin] = useState<string>('');
  const [showDriverPin, setShowDriverPin] = useState(false);
  
  // Dialog states
  const [shuttleDialogOpen, setShuttleDialogOpen] = useState(false);
  const [editingShuttle, setEditingShuttle] = useState<ShuttleBus | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [shuttleToDelete, setShuttleToDelete] = useState<string | null>(null);
  const [shuttleForm, setShuttleForm] = useState({
    name: '',
    capacity: 20,
    description: '',
    is_active: true,
  });

  const fetchUserClubs = useCallback(async () => {
    try {
      if (user?.is_platform_admin) {
        const response = await api.get('/clubs');
        setClubs(response.data);
        if (response.data.length > 0) {
          setSelectedClub(response.data[0].record_id);
        }
      } else {
        const response = await api.get('/me/clubs');
        const userClubs = Array.isArray(response.data) ? response.data : [response.data];
        setClubs(userClubs);
        if (userClubs.length > 0) {
          setSelectedClub(userClubs[0].record_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch clubs:', error);
      setError(t('shuttle.admin.errors.fetchClubs'));
    }
  }, [user, t]);

  const fetchShuttles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/clubs/${selectedClub}/shuttles`);
      setShuttles(response.data);
      
      // Fetch stats for each shuttle
      const stats: Record<string, ShuttleStats> = {};
      for (const shuttle of response.data) {
        try {
          const schedulesRes = await api.get(`/shuttles/${shuttle.id}/schedules`);
          const schedules = schedulesRes.data;
          
          // Calculate bookings across all schedules
          let totalBookings = 0;
          let todayBookings = 0;
          const today = new Date().toISOString().split('T')[0];
          
          for (const schedule of schedules) {
            try {
              const bookingsRes = await api.get(`/schedules/${schedule.id}/bookings`);
              const bookings = bookingsRes.data;
              totalBookings += bookings.length;
              
              // Check if this schedule is for today
              if (schedule.departure_date === today) {
                todayBookings += bookings.length;
              }
            } catch (err) {
              // Continue if individual schedule bookings fail
              console.error(`Failed to fetch bookings for schedule ${schedule.id}:`, err);
            }
          }
          
          stats[shuttle.id] = {
            totalSchedules: schedules.length,
            activeSchedules: schedules.filter((s: any) => new Date(s.departure_date) >= new Date()).length,
            totalBookings: totalBookings,
            todayBookings: todayBookings,
          };
        } catch (err) {
          stats[shuttle.id] = {
            totalSchedules: 0,
            activeSchedules: 0,
            totalBookings: 0,
            todayBookings: 0,
          };
        }
      }
      setShuttleStats(stats);
    } catch (error) {
      console.error('Failed to fetch shuttles:', error);
      setError(t('shuttle.admin.errors.fetchShuttles'));
    } finally {
      setLoading(false);
    }
  }, [selectedClub, t]);

  const fetchDriverPin = useCallback(async () => {
    try {
      const response = await api.get(`/clubs/${selectedClub}/driver-pin`);
      setDriverPin(response.data.pin || '');
    } catch (error) {
      console.error('Failed to fetch driver PIN:', error);
      setDriverPin('');
    }
  }, [selectedClub]);

  useEffect(() => {
    fetchUserClubs();
  }, [fetchUserClubs]);

  useEffect(() => {
    if (selectedClub) {
      fetchShuttles();
      fetchDriverPin();
    }
  }, [selectedClub, fetchShuttles, fetchDriverPin]);

  const handleClubChange = (event: any) => {
    setSelectedClub(event.target.value);
    setSelectedShuttle(null);
    setViewMode('list');
  };

  const handleSelectShuttle = (shuttle: ShuttleBus) => {
    setSelectedShuttle(shuttle);
    setViewMode('detail');
    setDetailTab('schedules');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedShuttle(null);
  };

  const handleOpenShuttleDialog = (shuttle?: ShuttleBus) => {
    if (shuttle) {
      setEditingShuttle(shuttle);
      setShuttleForm({
        name: shuttle.name,
        capacity: shuttle.capacity,
        description: shuttle.description || '',
        is_active: shuttle.is_active,
      });
    } else {
      setEditingShuttle(null);
      setShuttleForm({
        name: '',
        capacity: 20,
        description: '',
        is_active: true,
      });
    }
    setShuttleDialogOpen(true);
  };

  const handleCloseShuttleDialog = () => {
    setShuttleDialogOpen(false);
    setEditingShuttle(null);
  };

  const handleSaveShuttle = async () => {
    try {
      if (editingShuttle) {
        const payload = {
          ...shuttleForm,
          club_id: selectedClub,
        };
        await api.put(`/shuttles/${editingShuttle.id}`, payload);
        setSuccess(t('shuttle.admin.shuttleUpdated'));
      } else {
        const payload = {
          name: shuttleForm.name,
          capacity: shuttleForm.capacity,
          description: shuttleForm.description,
          is_active: shuttleForm.is_active,
        };
        await api.post(`/clubs/${selectedClub}/shuttles`, payload);
        setSuccess(t('shuttle.admin.shuttleCreated'));
      }
      
      handleCloseShuttleDialog();
      fetchShuttles();
    } catch (error) {
      console.error('Failed to save shuttle:', error);
      setError(t('shuttle.admin.errors.saveShuttle'));
    }
  };

  const handleDeleteShuttle = async () => {
    if (!shuttleToDelete) return;
    
    try {
      await api.delete(`/shuttles/${shuttleToDelete}`);
      setSuccess(t('shuttle.admin.shuttleDeleted'));
      setDeleteConfirmOpen(false);
      setShuttleToDelete(null);
      if (selectedShuttle?.id === shuttleToDelete) {
        handleBackToList();
      }
      fetchShuttles();
    } catch (error) {
      console.error('Failed to delete shuttle:', error);
      setError(t('shuttle.admin.errors.deleteShuttle'));
    }
  };

  const handleToggleActive = async (shuttle: ShuttleBus) => {
    try {
      await api.put(`/shuttles/${shuttle.id}`, {
        ...shuttle,
        is_active: !shuttle.is_active,
      });
      fetchShuttles();
    } catch (error) {
      console.error('Failed to toggle shuttle status:', error);
    }
  };


  const generateDriverPin = async () => {
    try {
      const response = await api.post(`/clubs/${selectedClub}/driver-pin/generate`);
      setDriverPin(response.data.pin);
      setSuccess(t('shuttle.admin.pinGenerated'));
      setShowDriverPin(true);
    } catch (error) {
      console.error('Failed to generate driver PIN:', error);
      setError(t('shuttle.admin.errors.generatePin'));
    }
  };

  const copyDriverLink = () => {
    const link = `${window.location.origin}/driver?club=${selectedClub}`;
    navigator.clipboard.writeText(link);
    setSuccess(t('shuttle.admin.linkCopied'));
  };

  const copyDriverLinkWithPin = () => {
    const link = `${window.location.origin}/driver?club=${selectedClub}`;
    const message = `${t('shuttle.admin.driverAccessLink')}:\n${link}\n\n${t('shuttle.admin.driverPin')}: ${driverPin}`;
    navigator.clipboard.writeText(message);
    setSuccess(t('shuttle.admin.linkAndPinCopied'));
  };

  // Render empty state
  if (clubs.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <DirectionsBus sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('shuttle.admin.noClubsTitle')}
          </Typography>
          <Typography color="text.secondary">
            {t('shuttle.admin.noClubsMessage')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Render list view
  const renderListView = () => (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('shuttle.admin.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>{t('shuttle.admin.selectClub')}</InputLabel>
            <Select
              value={selectedClub}
              onChange={handleClubChange}
              label={t('shuttle.admin.selectClub')}
            >
              {clubs.map((club) => (
                <MenuItem key={club.record_id} value={club.record_id}>
                  {club.club_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenShuttleDialog()}
            sx={{
              background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
            }}
          >
            {t('shuttle.admin.addShuttle')}
          </Button>
        </Box>
      </Box>

      {/* Driver Access Link */}
      {selectedClub && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: 'info.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Lock color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('shuttle.admin.driverAccess')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('shuttle.admin.driverAccessUrl')}:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                {window.location.origin}/driver?club={selectedClub}
              </Typography>
              
              {driverPin ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('shuttle.admin.driverPin')}:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: 'monospace',
                        letterSpacing: 2,
                        fontWeight: 600,
                        color: 'primary.main'
                      }}
                    >
                      {showDriverPin ? driverPin : '••••'}
                    </Typography>
                    <IconButton 
                      size="small"
                      onClick={() => setShowDriverPin(!showDriverPin)}
                    >
                      {showDriverPin ? <Visibility /> : <VpnKey />}
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('shuttle.admin.noPinGenerated')}
                </Alert>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={copyDriverLink}
                fullWidth
              >
                {t('shuttle.admin.copyLink')}
              </Button>
              {!driverPin ? (
                <Button
                  variant="contained"
                  startIcon={<VpnKey />}
                  onClick={generateDriverPin}
                  fullWidth
                >
                  {t('shuttle.admin.generatePin')}
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    startIcon={<ContentCopy />}
                    onClick={copyDriverLinkWithPin}
                    fullWidth
                  >
                    {t('shuttle.admin.copyLinkAndPin')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={generateDriverPin}
                    size="small"
                    fullWidth
                  >
                    {t('shuttle.admin.regeneratePin')}
                  </Button>
                </>
              )}
            </Box>
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {t('shuttle.admin.driverAccessHelp')}
          </Typography>
        </Paper>
      )}

      {/* Shuttles List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : shuttles.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <DirectionsBus sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('shuttle.admin.noShuttles')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('shuttle.admin.noShuttlesHint')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenShuttleDialog()}
          >
            {t('shuttle.admin.createFirstShuttle')}
          </Button>
        </Paper>
      ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            lg: 'repeat(3, 1fr)' 
          }, 
          gap: 3 
        }}>
          {shuttles.map((shuttle) => {
            const stats = shuttleStats[shuttle.id] || {
              totalSchedules: 0,
              activeSchedules: 0,
              totalBookings: 0,
              todayBookings: 0,
            };
            
            return (
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                  onClick={() => handleSelectShuttle(shuttle)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {shuttle.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('shuttle.admin.capacity')}: {shuttle.capacity} {t('shuttle.admin.seats')}
                        </Typography>
                      </Box>
                      <Chip
                        label={shuttle.is_active ? t('common.active') : t('common.inactive')}
                        color={shuttle.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    {shuttle.description && (
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {shuttle.description}
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarMonth fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('shuttle.admin.schedules')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {stats.activeSchedules}/{stats.totalSchedules}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('shuttle.admin.todayBookings')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {stats.todayBookings}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        size="small"
                        startIcon={<Settings />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectShuttle(shuttle);
                        }}
                        fullWidth
                      >
                        {t('shuttle.admin.manage')}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenShuttleDialog(shuttle);
                        }}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShuttleToDelete(shuttle.id);
                          setDeleteConfirmOpen(true);
                        }}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
            );
          })}
        </Box>
      )}
    </>
  );

  // Render detail view
  const renderDetailView = () => {
    if (!selectedShuttle) return null;
    
    return (
      <>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body1"
            onClick={handleBackToList}
            sx={{ cursor: 'pointer' }}
          >
            {t('shuttle.admin.allShuttles')}
          </Link>
          <Typography color="text.primary">{selectedShuttle.name}</Typography>
        </Breadcrumbs>

        {/* Shuttle Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <DirectionsBus fontSize="large" color="primary" />
                <Typography variant="h4">
                  {selectedShuttle.name}
                </Typography>
                <Chip
                  label={selectedShuttle.is_active ? t('common.active') : t('common.inactive')}
                  color={selectedShuttle.is_active ? 'success' : 'default'}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                {t('shuttle.admin.capacity')}: {selectedShuttle.capacity} {t('shuttle.admin.seats')}
              </Typography>
              {selectedShuttle.description && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedShuttle.description}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handleBackToList}
              >
                {t('common.back')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => handleOpenShuttleDialog(selectedShuttle)}
              >
                {t('common.edit')}
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedShuttle.is_active}
                    onChange={() => handleToggleActive(selectedShuttle)}
                    color="primary"
                  />
                }
                label={t('common.active')}
              />
            </Box>
          </Box>
        </Paper>

        {/* Detail Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
            <Button
              sx={{
                flex: 1,
                py: 2,
                borderRadius: 0,
                backgroundColor: detailTab === 'schedules' ? 'primary.main' : 'transparent',
                color: detailTab === 'schedules' ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: detailTab === 'schedules' ? 'primary.dark' : 'action.hover',
                },
              }}
              startIcon={<CalendarMonth />}
              onClick={() => setDetailTab('schedules')}
            >
              {t('shuttle.admin.tabs.schedules')}
            </Button>
            <Button
              sx={{
                flex: 1,
                py: 2,
                borderRadius: 0,
                backgroundColor: detailTab === 'bookings' ? 'primary.main' : 'transparent',
                color: detailTab === 'bookings' ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: detailTab === 'bookings' ? 'primary.dark' : 'action.hover',
                },
              }}
              startIcon={<BookOnline />}
              onClick={() => setDetailTab('bookings')}
            >
              {t('shuttle.admin.tabs.bookings')}
            </Button>
          </Box>
          
          <Box sx={{ p: 3 }}>
            {detailTab === 'schedules' ? (
              <ShuttleScheduler
                shuttle={selectedShuttle}
                onScheduleCreated={() => {
                  setSuccess(t('shuttle.admin.scheduleCreated'));
                  fetchShuttles();
                }}
                onScheduleUpdated={() => {
                  setSuccess(t('shuttle.admin.scheduleUpdated'));
                  fetchShuttles();
                }}
                onViewBookings={(scheduleId) => {
                  setSelectedScheduleId(scheduleId);
                  setDetailTab('bookings');
                }}
              />
            ) : (
              <ShuttleBookingsList
                shuttle={selectedShuttle}
                scheduleId={selectedScheduleId}
              />
            )}
          </Box>
        </Paper>
      </>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      {viewMode === 'list' ? renderListView() : renderDetailView()}

      {/* Shuttle Dialog */}
      <Dialog open={shuttleDialogOpen} onClose={handleCloseShuttleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingShuttle ? t('shuttle.admin.editShuttle') : t('shuttle.admin.addShuttle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('shuttle.admin.shuttleName')}
            fullWidth
            variant="outlined"
            value={shuttleForm.name}
            onChange={(e) => setShuttleForm({ ...shuttleForm, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('shuttle.admin.capacity')}
            type="number"
            fullWidth
            variant="outlined"
            value={shuttleForm.capacity}
            onChange={(e) => setShuttleForm({ ...shuttleForm, capacity: parseInt(e.target.value) || 0 })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('shuttle.admin.description')}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={shuttleForm.description}
            onChange={(e) => setShuttleForm({ ...shuttleForm, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={shuttleForm.is_active}
                onChange={(e) => setShuttleForm({ ...shuttleForm, is_active: e.target.checked })}
              />
            }
            label={t('shuttle.admin.activeShuttle')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShuttleDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSaveShuttle}
            variant="contained"
            disabled={!shuttleForm.name || shuttleForm.capacity < 1}
          >
            {editingShuttle ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('shuttle.admin.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('shuttle.admin.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteShuttle} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShuttleManagementPageV2;